import { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  IconNews,
  IconTags,
  IconRss,
  IconHome,
  IconMessageCircle,
  IconFileText,
  IconDashboard,
  IconFlame,
  IconAd,
  IconSpeakerphone,
  IconBuildingStore,
  IconTruck,
  IconFileInvoice,
  IconUsers,
  IconBell,
  IconTrees,
  IconTag,
  IconSettings,
  IconChartBar,
  IconMap2,
  IconLayersSubtract,
} from '@tabler/icons-react';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, signOut, activeDatabaseId, setActiveDatabase } = useAuth();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Close dropdown when navigating to a new page
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside the navbar
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  function toggleDropdown(name: string) {
    setOpenDropdown(prev => (prev === name ? null : name));
  }

  const newsItems = [
    { path: '/categories', label: 'Categories', icon: 'tag' },
    { path: '/rss-sources', label: 'Sources', icon: 'rss' },
    { path: '/news', label: 'Articles', icon: 'news' },
  ];

  const forumItems = [
    { path: '/forum-rooms', label: 'Rooms', icon: 'home' },
    { path: '/forum-topics', label: 'Topics', icon: 'message-circle' },
    { path: '/forum-posts', label: 'Posts', icon: 'file-text' },
  ];

  const advertisingItems = [
    { path: '/advertisers', label: 'Advertisers', icon: 'speakerphone' },
    { path: '/campaigns', label: 'Campaigns', icon: 'ad' },
  ];

  const marketItems = [
    { path: '/suppliers', label: 'Suppliers', icon: 'truck' },
    { path: '/quotes', label: 'Quotes', icon: 'file-invoice' },
    { path: '/forestry-operations', label: 'Forestry Operations', icon: 'trees' },
    { path: '/product-pricing', label: 'Product Pricing', icon: 'tag' },
  ];

  const isNewsActive = newsItems.some(item => location.pathname === item.path);
  const isForumActive = forumItems.some(item => location.pathname === item.path);
  const isMarketActive = marketItems.some(item => location.pathname === item.path);
  const isUsersActive = location.pathname === '/users' || location.pathname === '/notifications';
  const isAdvertisingActive = advertisingItems.some(
    item => location.pathname === item.path || location.pathname.startsWith('/advertisers/')
  );
  const isContentActive = isNewsActive || isForumActive || location.pathname === '/statistics';
  const isMapsActive =
    location.pathname.startsWith('/settings/map-layers') || location.pathname === '/fire-risk';

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <div className="page">
      {/* Navbar */}
      <header className="navbar navbar-expand-md navbar-light d-print-none">
        <div className="container-xl" ref={navRef}>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbar-menu"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <h1 className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3">
            <a href="." className="d-flex align-items-center">
              <img
                src="/logo-color.png"
                alt="ForestSIM Mobile"
                style={{ height: 28, width: 228, objectFit: 'contain' }}
              />
            </a>
          </h1>
          <div className="collapse navbar-collapse" id="navbar-menu">
            <ul className="navbar-nav">
              {/* Dashboard */}
              <li className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
                <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconDashboard className="icon" />
                  </span>
                  <span className="nav-link-title">Dashboard</span>
                </Link>
              </li>

              {/* Content Dropdown: News, Forum, Statistics */}
              <li
                className={`nav-item dropdown ${isContentActive ? 'active' : ''} ${openDropdown === 'content' ? 'show' : ''}`}
              >
                <a
                  className={`nav-link dropdown-toggle ${isContentActive ? 'active' : ''}`}
                  href="#"
                  role="button"
                  aria-expanded={openDropdown === 'content'}
                  onClick={e => {
                    e.preventDefault();
                    toggleDropdown('content');
                  }}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconNews className="icon" />
                  </span>
                  <span className="nav-link-title">Content</span>
                </a>
                <div
                  className={`dropdown-menu ${openDropdown === 'content' ? 'show' : ''}`}
                  style={{ top: '100%', bottom: 'auto' }}
                >
                  <div className="dropdown-header">News</div>
                  {newsItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.icon === 'tag' && <IconTags className="icon dropdown-item-icon" />}
                      {item.icon === 'rss' && <IconRss className="icon dropdown-item-icon" />}
                      {item.icon === 'news' && <IconNews className="icon dropdown-item-icon" />}
                      {item.label}
                    </Link>
                  ))}
                  <div className="dropdown-divider" />
                  <div className="dropdown-header">Forum</div>
                  {forumItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.icon === 'home' && <IconHome className="icon dropdown-item-icon" />}
                      {item.icon === 'message-circle' && (
                        <IconMessageCircle className="icon dropdown-item-icon" />
                      )}
                      {item.icon === 'file-text' && (
                        <IconFileText className="icon dropdown-item-icon" />
                      )}
                      {item.label}
                    </Link>
                  ))}
                  <div className="dropdown-divider" />
                  <Link
                    to="/statistics"
                    className={`dropdown-item ${location.pathname === '/statistics' ? 'active' : ''}`}
                  >
                    <IconChartBar className="icon dropdown-item-icon" />
                    Statistics
                  </Link>
                </div>
              </li>

              {/* Market Dropdown */}
              <li
                className={`nav-item dropdown ${isMarketActive ? 'active' : ''} ${openDropdown === 'market' ? 'show' : ''}`}
              >
                <a
                  className={`nav-link dropdown-toggle ${isMarketActive ? 'active' : ''}`}
                  href="#"
                  role="button"
                  aria-expanded={openDropdown === 'market'}
                  onClick={e => {
                    e.preventDefault();
                    toggleDropdown('market');
                  }}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconBuildingStore className="icon" />
                  </span>
                  <span className="nav-link-title">Market</span>
                </a>
                <div
                  className={`dropdown-menu ${openDropdown === 'market' ? 'show' : ''}`}
                  style={{ top: '100%', bottom: 'auto' }}
                >
                  {marketItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.icon === 'truck' && <IconTruck className="icon dropdown-item-icon" />}
                      {item.icon === 'file-invoice' && (
                        <IconFileInvoice className="icon dropdown-item-icon" />
                      )}
                      {item.icon === 'trees' && <IconTrees className="icon dropdown-item-icon" />}
                      {item.icon === 'tag' && <IconTag className="icon dropdown-item-icon" />}
                      {item.label}
                    </Link>
                  ))}
                </div>
              </li>

              {/* Advertising Dropdown */}
              <li
                className={`nav-item dropdown ${isAdvertisingActive ? 'active' : ''} ${openDropdown === 'advertising' ? 'show' : ''}`}
              >
                <a
                  className={`nav-link dropdown-toggle ${isAdvertisingActive ? 'active' : ''}`}
                  href="#"
                  role="button"
                  aria-expanded={openDropdown === 'advertising'}
                  onClick={e => {
                    e.preventDefault();
                    toggleDropdown('advertising');
                  }}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconAd className="icon" />
                  </span>
                  <span className="nav-link-title">Advertising</span>
                </a>
                <div
                  className={`dropdown-menu ${openDropdown === 'advertising' ? 'show' : ''}`}
                  style={{ top: '100%', bottom: 'auto' }}
                >
                  {advertisingItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`dropdown-item ${
                        location.pathname === item.path ||
                        (item.path === '/advertisers' &&
                          location.pathname.startsWith('/advertisers/'))
                          ? 'active'
                          : ''
                      }`}
                    >
                      {item.icon === 'speakerphone' && (
                        <IconSpeakerphone className="icon dropdown-item-icon" />
                      )}
                      {item.icon === 'ad' && <IconAd className="icon dropdown-item-icon" />}
                      {item.label}
                    </Link>
                  ))}
                </div>
              </li>

              {/* Users Dropdown */}
              <li
                className={`nav-item dropdown ${isUsersActive ? 'active' : ''} ${openDropdown === 'users' ? 'show' : ''}`}
              >
                <a
                  className={`nav-link dropdown-toggle ${isUsersActive ? 'active' : ''}`}
                  href="#"
                  role="button"
                  aria-expanded={openDropdown === 'users'}
                  onClick={e => {
                    e.preventDefault();
                    toggleDropdown('users');
                  }}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconUsers className="icon" />
                  </span>
                  <span className="nav-link-title">Users</span>
                </a>
                <div
                  className={`dropdown-menu ${openDropdown === 'users' ? 'show' : ''}`}
                  style={{ top: '100%', bottom: 'auto' }}
                >
                  <Link
                    to="/users"
                    className={`dropdown-item ${location.pathname === '/users' ? 'active' : ''}`}
                  >
                    <IconUsers className="icon dropdown-item-icon" />
                    Users
                  </Link>
                  <Link
                    to="/notifications"
                    className={`dropdown-item ${location.pathname === '/notifications' ? 'active' : ''}`}
                  >
                    <IconBell className="icon dropdown-item-icon" />
                    Notifications
                  </Link>
                </div>
              </li>

              {/* Maps Dropdown: Map Layers, Fire Risk */}
              <li
                className={`nav-item dropdown ${isMapsActive ? 'active' : ''} ${openDropdown === 'maps' ? 'show' : ''}`}
              >
                <a
                  className={`nav-link dropdown-toggle ${isMapsActive ? 'active' : ''}`}
                  href="#"
                  role="button"
                  aria-expanded={openDropdown === 'maps'}
                  onClick={e => {
                    e.preventDefault();
                    toggleDropdown('maps');
                  }}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconMap2 className="icon" />
                  </span>
                  <span className="nav-link-title">Maps</span>
                </a>
                <div
                  className={`dropdown-menu ${openDropdown === 'maps' ? 'show' : ''}`}
                  style={{ top: '100%', bottom: 'auto' }}
                >
                  <Link
                    to="/settings/map-layers"
                    className={`dropdown-item ${location.pathname.startsWith('/settings/map-layers') ? 'active' : ''}`}
                  >
                    <IconLayersSubtract className="icon dropdown-item-icon" />
                    Map Layers
                  </Link>
                  <Link
                    to="/fire-risk"
                    className={`dropdown-item ${location.pathname === '/fire-risk' ? 'active' : ''}`}
                  >
                    <IconFlame className="icon dropdown-item-icon" />
                    Fire Risk
                  </Link>
                </div>
              </li>

              {/* Settings */}
              <li
                className={`nav-item ${location.pathname === '/settings/email-templates' ? 'active' : ''}`}
              >
                <Link
                  to="/settings/email-templates"
                  className={`nav-link ${location.pathname === '/settings/email-templates' ? 'active' : ''}`}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <IconSettings className="icon" />
                  </span>
                  <span className="nav-link-title">Settings</span>
                </Link>
              </li>
            </ul>
          </div>
          <div className="navbar-nav flex-row order-md-last align-items-center gap-3">
            <div className="nav-item d-flex align-items-center">
              <select
                className={`form-select form-select-sm${activeDatabaseId !== '(default)' ? ' border-warning text-warning fw-bold' : ''}`}
                style={{ minWidth: 110 }}
                value={activeDatabaseId}
                onChange={e => {
                  setActiveDatabase(e.target.value);
                  window.location.reload();
                }}
              >
                <option value="(default)">Production</option>
                <option value="test">Test</option>
              </select>
            </div>
            <div className="nav-item dropdown">
              <a
                href="#"
                className="nav-link d-flex lh-1 text-reset p-0"
                role="button"
                aria-expanded={openDropdown === 'user'}
                onClick={e => {
                  e.preventDefault();
                  toggleDropdown('user');
                }}
              >
                <span className="avatar avatar-sm">{user?.email?.[0].toUpperCase()}</span>
              </a>
              <div
                className={`dropdown-menu dropdown-menu-end dropdown-menu-arrow ${openDropdown === 'user' ? 'show' : ''}`}
                style={{ minWidth: 220, position: 'absolute', top: '100%', bottom: 'auto' }}
              >
                <div className="dropdown-item-text py-2">
                  {user?.displayName && <div className="fw-medium">{user.displayName}</div>}
                  <div className={user?.displayName ? 'text-muted small' : 'fw-medium'}>
                    {user?.email}
                  </div>
                </div>
                <div className="dropdown-divider" />
                <button onClick={handleSignOut} className="dropdown-item text-danger">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Page content */}
      <div className="page-wrapper">
        <div className="container-xl">{children}</div>
      </div>
    </div>
  );
}
